import pluralize from "pluralize"
import { Product, ColourVariation } from "@/types"

export const getProductBase = (product: Product | ColourVariation) => {
  if (product.product_type === "fabric") {
    return { path: "fabrics", name: "Fabrics" }
  }

  if (product.product_type === "tooling") {
    return { path: "tooling", name: "Tooling" }
  }

  if (product.product_type === "point-of-sale") {
    return { path: "point-of-sale", name: "Point of Sale" }
  }

  return { path: "components", name: "Components" }
}

export const getProductUrl = (product: Product | ColourVariation) => {
  const { path } = getProductBase(product)

  return `/${path}/${product.slug}/`
}

/**
 * Product types that only allow 'Box' as quantity unit.
 */
const BOX_ONLY_PRODUCT_TYPES = new Set(["point-of-sale", "tooling"])

export const getProductDefaultQuantityUnit = (product: Product) => {
  if (BOX_ONLY_PRODUCT_TYPES.has(product.product_type)) {
    return "Box"
  }

  if (product.product_type === "components") {
    if (Object.keys(product.supported_units).length === 0) {
      return undefined
    }

    return product.cut_length_warehouse
      ? product.stock_unit.replaceAll(/\d/g, "")
      : "Box"
  }

  return undefined
}

export const getProductDefaultQuantity = (
  product: Product,
  { defaults }: { defaults?: boolean } = {},
): number => {
  const quantity = 0

  if (product.product_type === "components") {
    if (Object.keys(product.supported_units).length === 0) {
      return 1
    }

    if (defaults) {
      if (product.cut_length_warehouse) {
        return Number(product.cut_length_unit.replaceAll(/[^\d.]/g, "")) || 1
      }
      return 1
    }

    return 0
  }

  if (["fabric"].includes(product.product_type)) {
    return 0
  }

  return quantity > 0 ? quantity : 1
}

const LENGTH_SYMBOLS = new Set(["mm", "cm", "m", "ft"])

/** Display only: never compare the result with a stored unit. */
export const formatUnit = (unit: string) =>
  unit.replaceAll(/[a-z]+/gi, (word) => {
    const lower = word.toLowerCase()

    return LENGTH_SYMBOLS.has(lower)
      ? lower
      : lower.charAt(0).toUpperCase() + lower.slice(1)
  })

const parseUnit = (unit: string) => ({
  value: Number(unit.replaceAll(/[^\d.]/g, "")),
  suffix: formatUnit(unit.replaceAll(/[\d.]/g, "").trim()),
})

export const getCutLength = (
  product: Product,
  unit?: string | null,
): { value: number; suffix: string } | null => {
  if (!unit || unit === "Box") {
    return null
  }

  const { value, suffix } = parseUnit(product.cut_length_unit)

  return value > 0 && suffix ? { value, suffix } : null
}

export const getPackSize = (product: Product): number | null => {
  const { value, suffix } = parseUnit(product.cut_length_unit)

  return value > 0 && !suffix ? value : null
}

/** Word units are lowercased and pluralised ("3 sets"); length symbols pass through. */
export const formatQuantityUnit = (
  unit: string | null | undefined,
  quantity: number,
) => {
  if (!unit) {
    return undefined
  }

  const word = unit.toLowerCase()

  if (LENGTH_SYMBOLS.has(word) || word === "each") {
    return word
  }

  return pluralize(word, quantity)
}

export const roundLength = (length: number) => Math.round(length * 1000) / 1000

export const formatLength = (length: number, suffix: string) =>
  `${roundLength(length)}${suffix}`

/** Returns null when the sales unit carries no amount, e.g. "Each". Counts in the stock unit, not the sales unit ("25 Pack" of loops). */
export const getBoxUnit = (product: Product) => {
  const value = Number(product.sales_unit.replaceAll(/[^\d.]/g, ""))

  if (!value) {
    return null
  }

  const unit = formatUnit(product.stock_unit.replaceAll(/[\d.]/g, "").trim())
  const isLength = LENGTH_SYMBOLS.has(unit)

  return {
    value,
    isLength,
    format: (amount: number) =>
      isLength
        ? formatLength(amount, unit)
        : `${roundLength(amount)} ${formatQuantityUnit(unit, amount)}`,
  }
}
